import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDateBR, formatDateTimeBR, formatBRL, todayBR } from './format';

/*
 * As datas do PDF vêm de `format.js`, a fonte única do fuso do sistema.
 *
 * Até 17/08/2026 este arquivo tinha a própria conversão:
 *
 *     new Date("2026-06-13").toLocaleDateString('pt-BR')
 *
 * `new Date` de uma data sem hora é lida como MEIA-NOITE UTC; exibida em
 * UTC-3, vira o dia anterior. Todo relatório saía com as datas um dia atrás
 * — o bug D-1 clássico, que `format.js` já resolvia e documentava.
 *
 * A causa não foi o cálculo, foi a DUPLICAÇÃO: duas implementações da mesma
 * conversão, e só uma estava certa. Por isso aqui não há função de data
 * própria, nem deve voltar a haver.
 */
const brl = formatBRL;
const br = formatDateBR;

export function exportTransactionsPDF(items, projectName, {
  pots = [], accounts = [], entities = [], sources = [],
  visibleCols = null,
} = {}) {
  // Landscape orientation for more horizontal space
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const potName = (id) => pots.find((p) => p.id === id)?.name || '';
  const accName = (id) => accounts.find((a) => a.id === id)?.name || '';
  const entName = (id) => entities.find((e) => e.id === id)?.name || '';
  const srcName = (id) => sources.find((s) => s.id === id)?.name || '';

  // If visibleCols not provided, fall back to a sensible default set
  const cols = visibleCols instanceof Set
    ? visibleCols
    : new Set(['date', 'description', 'pot_exit', 'pot_entry', 'account_exit', 'account_entry', 'tags', 'amount']);

  // Build columns dynamically in the same order as the on-screen table.
  // 'tags' is special: it appears inline below the description (not as its own column).
  const colDefs = [
    { id: 'date', label: 'Data', width: 60 },
    { id: 'description', label: 'Descrição' /* auto width */ },
    { id: 'pot_exit', label: 'Pote Saída', width: 75 },
    { id: 'pot_entry', label: 'Pote Entrada', width: 75 },
    { id: 'account_exit', label: 'Conta Saída', width: 75 },
    { id: 'account_entry', label: 'Conta Entrada', width: 75 },
    { id: 'entity', label: 'Pessoas', width: 70 },
    { id: 'source', label: 'Fonte de Receita', width: 85 },
    { id: 'amount', label: 'Valor', width: 80, halign: 'right' },
  ].filter((c) => cols.has(c.id));

  const head = [colDefs.map((c) => c.label)];
  const showTags = cols.has('tags');

  const body = items.map((t) => colDefs.map((c) => {
    switch (c.id) {
      case 'date': return br(t.transaction_date);
      case 'description': {
        const typeLabel = t.type === 'income' ? '+ ' : t.type === 'expense' ? '- ' : '↔ ';
        let out = `${typeLabel}${t.description || '-'}`;
        if (t.notes) out += `\n${t.notes}`;
        if (showTags && t.tags?.length) {
          out += `\n[${t.tags.map((tg) => tg.name).join(', ')}]`;
        }
        return out;
      }
      case 'pot_exit': return potName(t.pot_exit_id);
      case 'pot_entry': return potName(t.pot_entry_id);
      case 'account_exit': return accName(t.account_exit_id);
      case 'account_entry': return accName(t.account_entry_id);
      case 'entity': return entName(t.entity_id);
      case 'source': return srcName(t.revenue_source_id);
      case 'amount': {
        const prefix = t.type === 'expense' ? '-' : t.type === 'income' ? '+' : '';
        return `${prefix}${brl(t.amount)}`;
      }
      default: return '';
    }
  }));

  // columnStyles: apply widths and alignment
  const columnStyles = {};
  colDefs.forEach((c, i) => {
    const s = {};
    if (c.width) s.cellWidth = c.width;
    if (c.halign) s.halign = c.halign;
    if (Object.keys(s).length) columnStyles[i] = s;
  });

  doc.setFontSize(14);
  doc.text(`D'Pots - Transações${projectName ? ' - ' + projectName : ''}`, 28, 30);
  doc.setFontSize(9);
  doc.text(`Gerado em ${formatDateTimeBR(new Date())} · ${items.length} ${items.length === 1 ? 'transação' : 'transações'}`, 28, 44);

  autoTable(doc, {
    startY: 56,
    head, body,
    styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak', valign: 'top' },
    headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
    columnStyles,
    margin: { left: 28, right: 28 },
  });

  // Resumo. Transferências entram numa linha própria: elas não somam nem
  // subtraem patrimônio (é dinheiro mudando de lugar), então misturá-las
  // com receita ou despesa daria um saldo errado. Mas omiti-las fazia o
  // resumo de uma seleção só de transferências aparecer zerado.
  let income = 0, expense = 0, transfer = 0, transferCount = 0;
  for (const t of items) {
    if (t.type === 'income') income += t.amount || 0;
    else if (t.type === 'expense') expense += t.amount || 0;
    else if (t.type === 'transfer') { transfer += t.amount || 0; transferCount += 1; }
  }
  let finalY = doc.lastAutoTable.finalY + 18;
  doc.setFontSize(10);
  doc.text(`Receitas: ${brl(income)}    Despesas: ${brl(expense)}    Saldo: ${brl(income - expense)}`, 28, finalY);
  if (transferCount > 0) {
    finalY += 14;
    doc.setFontSize(9);
    doc.text(
      `Transferências: ${brl(transfer)} em ${transferCount} ${transferCount === 1 ? 'lançamento' : 'lançamentos'} `
      + `(movimentação entre potes/contas — não altera o saldo acima)`,
      28, finalY,
    );
  }

  doc.save(`transacoes_${todayBR()}.pdf`);
}

export function exportPayableReceivablePDF(items, projectName) {
  const doc = new jsPDF();
  doc.setFontSize(16);
  doc.text(`D'Pots - A Pagar/Receber${projectName ? ' - ' + projectName : ''}`, 14, 16);
  doc.setFontSize(10);
  doc.text(`Gerado em ${formatDateTimeBR(new Date())}`, 14, 22);
  autoTable(doc, {
    startY: 28,
    head: [['Tipo', 'Descrição', 'Vencimento', 'Status', 'Valor']],
    body: items.map((it) => [
      it.type === 'payable' ? 'A Pagar' : 'A Receber',
      it.description || '-',
      br(it.due_date),
      it.status === 'pending' ? 'Pendente' : it.status === 'paid' ? 'Pago' : it.status === 'received' ? 'Recebido' : it.status,
      brl(it.amount),
    ]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [99, 102, 241], textColor: 255 },
    columnStyles: { 4: { halign: 'right' } },
  });
  doc.save(`apagar_receber_${todayBR()}.pdf`);
}
